// ─── Impostazioni → Regole per i soci → «Tessera associativa» ───────────────
//
// 16/10/2026, mockup «Tessera associativa» (v9), approvato col «vai». Dal
// committente, lo stesso giorno: *«questa sottosezione mettila dentro la sezione
// Regole per i soci, con una tab dedicata, chiamata tessera associativa»*.
//
// Due riquadri: ① le impostazioni (mostrarla nel bot, il link del modulo, la
// casella delle mail WindDoc) e ② le mail «Nuova Iscrizione» che non si sono
// abbinate da sole a un socio, con «Scegli il socio» e «Ignora».
//
// 📄 23/10/2026, mockup «Tessera e certificato medico»: il prezzo, «Serve la tessera per prenotare dal bot»,
// «Pagamento con la carta: simulazione» (solo per il gestionale di prova), e due elenchi: i certificati da
// controllare e i soci in pausa oggi.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { euroDellaTessera, type ElencoSoci, type MailTessera, type PaginaTessera } from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { Attesa, Campo, Guasto } from '../../components/pezzi.js';
import { SoloLettura, usaModifica } from './comune.js';

const CHIAVE = 'tessera';
const CHIAVE_MAIL = 'tessera-mail';

const quando = (iso: string) =>
  new Date(iso).toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

export function Tessera(): React.JSX.Element {
  const modificabile = usaModifica('manage_settings');
  const letta = useQuery({
    queryKey: [CHIAVE],
    queryFn: () => chiama<PaginaTessera>('GET', '/settings/tessera'),
  });
  if (letta.isLoading) return <Attesa cosa="la tessera associativa" />;
  if (letta.error || !letta.data) return <Guasto errore={letta.error} />;
  return (
    <>
      <Impostazioni salvate={letta.data} modificabile={modificabile} />
      <Certificati pagina={letta.data} />
      <DaAbbinare modificabile={modificabile} />
    </>
  );
}

function Impostazioni({ salvate, modificabile }: { salvate: PaginaTessera; modificabile: boolean }) {
  const qc = useQueryClient();
  const [attiva, setAttiva] = useState(salvate.attiva);
  const [link, setLink] = useState(salvate.linkModulo);
  const [prezzo, setPrezzo] = useState(euroScritti(salvate.prezzoCents));
  const [serve, setServe] = useState(salvate.servePerPrenotare);
  const [simulato, setSimulato] = useState(salvate.pagamentoSimulato);
  const [salvato, setSalvato] = useState(false);
  useEffect(() => {
    setAttiva(salvate.attiva);
    setLink(salvate.linkModulo);
    setPrezzo(euroScritti(salvate.prezzoCents));
    setServe(salvate.servePerPrenotare);
    setSimulato(salvate.pagamentoSimulato);
  }, [salvate]);
  const linkBuono = /^https:\/\/\S+\.\S+/.test(link.trim());
  const prezzoCents = centesimi(prezzo);

  const salva = useMutation({
    meta: { fatto: 'tessera associativa salvata' },
    mutationFn: () =>
      chiama<PaginaTessera>('PUT', '/settings/tessera', {
        attiva,
        linkModulo: link.trim(),
        prezzoCents,
        servePerPrenotare: serve,
        accesaIl: salvate.accesaIl,
        pagamentoSimulato: simulato,
      }),
    onSuccess: (dopo) => {
      qc.setQueryData([CHIAVE], dopo);
      setSalvato(true);
    },
  });

  return (
    <section>
      <header className="testata">
        <h3>Tessera associativa</h3>
        <SoloLettura mostra={modificabile} />
      </header>
      <form
        className="bot-soci"
        onSubmit={(e) => {
          e.preventDefault();
          if (linkBuono && prezzoCents !== null && modificabile) salva.mutate();
        }}
      >
        <fieldset className="riquadro" disabled={!modificabile}>
          <label className="spunta-grande">
            <input
              type="checkbox"
              checked={attiva}
              onChange={(e) => {
                setSalvato(false);
                setAttiva(e.target.checked);
              }}
            />{' '}
            Mostra la tessera nel bot
          </label>
          <p className="tenue piccolo">
            Il socio la trova in «👤 Profilo › 🪪 Tessera associativa»: la quota, il modulo e il certificato
            medico.
          </p>
          <Campo
            etichetta="Link del modulo"
            value={link}
            onChange={(e) => {
              setSalvato(false);
              setLink(e.target.value);
            }}
          />
          {!linkBuono && <p className="guasto">Il link deve cominciare con https://</p>}
          <Campo
            etichetta="Prezzo della tessera, all’anno (€)"
            value={prezzo}
            inputMode="decimal"
            onChange={(e) => {
              setSalvato(false);
              setPrezzo(e.target.value);
            }}
          />
          {prezzoCents === null && (
            <p className="guasto">Scrivi il prezzo in euro, per esempio 15 o 12,50.</p>
          )}
          <label className="spunta-grande">
            <input
              type="checkbox"
              checked={serve}
              onChange={(e) => {
                setSalvato(false);
                setServe(e.target.checked);
              }}
            />{' '}
            Serve la tessera per prenotare dal bot
          </label>
          <p className="tenue piccolo">
            {serve
              ? 'Acceso: chi non ha la tessera, o l’ha scaduta, prenota dal bot una volta sola, una partita o una lezione; dalla seconda serve la tessera pagata. Chi fa la tessera ha 60 giorni dal pagamento per mandare il certificato medico: passati i 60 giorni, prenota solo se l’ha mandato. Il campo fisso vuole sempre la tessera. Entrare nella partita di un altro resta libero per tutti.'
              : 'Spento: dal bot prenotano tutti, anche senza tessera. Dal giorno in cui lo accendi, chi fa la tessera ha 60 giorni dal pagamento per mandare il certificato medico.'}
            {salvate.accesaIl &&
              ` Acceso la prima volta il ${salvate.accesaIl.split('-').reverse().join('/')}.`}
          </p>
          <label className="spunta-grande">
            <input
              type="checkbox"
              checked={simulato}
              onChange={(e) => {
                setSalvato(false);
                setSimulato(e.target.checked);
              }}
            />{' '}
            🧪 Pagamento con la carta: simulazione
          </label>
          <p className="tenue piccolo">
            Solo per il gestionale di prova: «💳 Paga» nel bot apre una pagina che non chiede nessuna carta e
            scrive in cassa un incasso segnato «simulato». Spento, il socio paga in Segreteria.
          </p>
          <dl className="piccolo">
            <dt>Validità</dt>
            <dd>1 anno dalla compilazione del modulo (o dal pagamento in Segreteria, senza modulo)</dd>
            <dt>Certificato medico</dt>
            <dd>
              entro 60 giorni dal pagamento · vale un anno dalla data di rilascio · promemoria 7 giorni prima,
              e 30 e 7 giorni prima che scada
            </dd>
            <dt>Avviso prima della scadenza</dt>
            <dd>7 giorni prima · una volta sola</dd>
            <dt>Casella delle mail WindDoc</dt>
            <dd>
              solo le mail «Nuova Iscrizione» di noreply@winddoc.com · letta ogni 5 minuti ·{' '}
              {salvate.casellaCollegata ? (
                <>
                  ✅ collegata
                  {salvate.ultimaLettura ? `, ultima lettura ${quando(salvate.ultimaLettura)}` : ''}
                </>
              ) : (
                <strong>da collegare: la collega il team di deploy sul server</strong>
              )}
            </dd>
          </dl>
        </fieldset>
        {modificabile && (
          <>
            <Guasto errore={salva.error} />
            {salvato && !salva.isPending && <p className="tenue">Salvato.</p>}
            <button type="submit" disabled={salva.isPending || !linkBuono || prezzoCents === null}>
              {salva.isPending ? 'Un attimo…' : 'Salva'}
            </button>
          </>
        )}
      </form>
    </section>
  );
}

function DaAbbinare({ modificabile }: { modificabile: boolean }) {
  const qc = useQueryClient();
  const [sceglie, setSceglie] = useState<string | null>(null);
  const mail = useQuery({
    queryKey: [CHIAVE_MAIL],
    queryFn: () => chiama<{ items: MailTessera[] }>('GET', '/tessera/mail'),
  });
  const aggiorna = (dopo: { items: MailTessera[] }) => {
    qc.setQueryData([CHIAVE_MAIL], dopo);
    void qc.invalidateQueries({ queryKey: [CHIAVE] });
    setSceglie(null);
  };
  const ignora = useMutation({
    meta: { fatto: 'mail ignorata' },
    mutationFn: (id: string) => chiama<{ items: MailTessera[] }>('POST', `/tessera/mail/${id}/ignora`),
    onSuccess: aggiorna,
  });
  const abbina = useMutation({
    meta: { fatto: 'tessera registrata' },
    mutationFn: (v: { id: string; memberId: string }) =>
      chiama<{ items: MailTessera[] }>('POST', `/tessera/mail/${v.id}/abbina`, { memberId: v.memberId }),
    onSuccess: aggiorna,
  });

  if (!mail.data) return <Guasto errore={mail.error} />;
  const daFare = mail.data.items.filter((m) => m.stato === 'da_abbinare');
  const fatte = mail.data.items.filter((m) => m.stato !== 'da_abbinare');

  return (
    <section className="riquadro">
      <h3>
        🪪 Tessere da abbinare {daFare.length > 0 && <span className="da-leggere">{daFare.length}</span>}
      </h3>
      {daFare.length === 0 ? (
        <p className="tenue piccolo">Tutte le mail di WindDoc sono abbinate a un socio.</p>
      ) : (
        <p className="tenue piccolo">
          Da abbinare a mano: nome ed email non corrispondono a un socio, e il modulo è stato aperto fuori dal
          bot.
        </p>
      )}
      <ul className="storico">
        {daFare.map((m) => (
          <li key={m.id}>
            <strong>
              {m.nome} {m.cognome}
            </strong>{' '}
            · {m.email || 'senza email'} · {quando(m.ricevuta)}
            {modificabile && sceglie !== m.id && (
              <>
                {' '}
                <button type="button" className="bordato piccolo" onClick={() => setSceglie(m.id)}>
                  Scegli il socio
                </button>{' '}
                <button
                  type="button"
                  className="bordato piccolo"
                  disabled={ignora.isPending}
                  onClick={() => ignora.mutate(m.id)}
                >
                  Ignora
                </button>
              </>
            )}
            {sceglie === m.id && (
              <SceltaDelSocio
                cerca={`${m.nome} ${m.cognome}`}
                inCorso={abbina.isPending}
                onScelto={(memberId) => abbina.mutate({ id: m.id, memberId })}
                onAnnulla={() => setSceglie(null)}
              />
            )}
          </li>
        ))}
      </ul>
      <Guasto errore={abbina.error ?? ignora.error} />
      {fatte.length > 0 && (
        <details>
          <summary className="piccolo">Le ultime mail lette ({fatte.length})</summary>
          <ul className="storico piccolo">
            {fatte.map((m) => (
              <li key={m.id}>
                {quando(m.ricevuta)} · {m.nome} {m.cognome} ·{' '}
                {m.stato === 'abbinata' ? `✅ ${m.socio?.nome ?? 'socio'}` : 'ignorata'}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

function SceltaDelSocio({
  cerca,
  inCorso,
  onScelto,
  onAnnulla,
}: {
  cerca: string;
  inCorso: boolean;
  onScelto: (memberId: string) => void;
  onAnnulla: () => void;
}) {
  const [q, setQ] = useState(cerca);
  const soci = useQuery({
    queryKey: ['tessera-cerca', q],
    queryFn: () =>
      chiama<ElencoSoci>('GET', `/members?${new URLSearchParams({ q: q.trim(), limit: '8' }).toString()}`),
    enabled: q.trim().length >= 2,
  });
  return (
    <div className="riquadro">
      <Campo etichetta="Cerca il socio" value={q} onChange={(e) => setQ(e.target.value)} />
      <ul className="storico">
        {(soci.data?.items ?? []).map((s) => (
          <li key={s.id}>
            {s.displayName} {s.email ? `· ${s.email}` : ''}{' '}
            <button
              type="button"
              className="bordato piccolo"
              disabled={inCorso}
              onClick={() => onScelto(s.id)}
            >
              Abbina
            </button>
          </li>
        ))}
      </ul>
      {soci.data && soci.data.items.length === 0 && <p className="tenue piccolo">Prova con meno lettere.</p>}
      <button type="button" className="bordato piccolo" onClick={onAnnulla}>
        Lascia stare
      </button>
    </div>
  );
}

/** «15» o «12,50» ⇒ centesimi; `null` se non è un prezzo. */
function centesimi(s: string): number | null {
  const t = s.trim().replace(/\s*€$/, '').replace(',', '.');
  if (!/^\d{1,4}(\.\d{1,2})?$/.test(t)) return null;
  return Math.round(Number(t) * 100);
}

const euroScritti = (cents: number) => euroDellaTessera(cents).replace(' €', '');

/** 📄 I certificati arrivati da guardare, e i soci in pausa oggi. Il nome apre la scheda. */
function Certificati({ pagina }: { pagina: PaginaTessera }): React.JSX.Element {
  const quando = (iso: string) =>
    new Date(iso).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  const giorno = (d: string) => d.split('-').reverse().join('/');
  return (
    <section>
      <header className="testata">
        <h3>📄 Certificati da controllare</h3>
      </header>
      {pagina.certificatiDaControllare.length === 0 ? (
        <p className="tenue">Tutti i certificati arrivati sono stati guardati.</p>
      ) : (
        <table className="tabella">
          <thead>
            <tr>
              <th>Socio</th>
              <th>Arrivato</th>
              <th>Rilascio</th>
              <th>Valido fino al</th>
            </tr>
          </thead>
          <tbody>
            {pagina.certificatiDaControllare.map((c) => (
              <tr key={c.memberId}>
                <td>
                  <Link to={`/soci?socio=${c.memberId}`}>{c.nome}</Link>
                </td>
                <td>{quando(c.arrivato)}</td>
                <td>{giorno(c.rilascio)}</td>
                <td>{giorno(c.validoFino)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <header className="testata">
        <h3>⏸️ In pausa oggi</h3>
      </header>
      {!pagina.servePerPrenotare ? (
        <p className="tenue">La regola è spenta: tutti prenotano come prima.</p>
      ) : pagina.inPausa.length === 0 ? (
        <p className="tenue">Tutti i soci con la tessera prenotano dal bot.</p>
      ) : (
        <table className="tabella">
          <thead>
            <tr>
              <th>Socio</th>
              <th>Tessera pagata il</th>
              <th>Perché</th>
            </tr>
          </thead>
          <tbody>
            {pagina.inPausa.map((s) => (
              <tr key={s.memberId}>
                <td>
                  <Link to={`/soci?socio=${s.memberId}`}>{s.nome}</Link>
                </td>
                <td>{s.pagataIl ? giorno(s.pagataIl) : '—'}</td>
                <td>{s.perche}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
